import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'

/**
 * History subsection adapter
 * Handles 60-day history tracking and medical/surgical history (14 fields)
 */
export class HistoryAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // 60-day history fields
    const history60DayEr = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_ER
    )?.at(0)

    const historyErNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_ER_NOTES
    )?.at(0)

    const history60DayHospital = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_HOSPITAL
    )?.at(0)

    const historyHospitalNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_HOSPITAL_NOTES
    )?.at(0)

    const history60DayInjuries = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_INJURIES
    )?.at(0)

    const historyInjuryNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_INJURY_NOTES
    )?.at(0)

    const history60DayFalls = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_FALLS
    )?.at(0)

    const historyFallNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_FALL_NOTES
    )?.at(0)

    const history60DayInfections = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_INFECTIONS
    )?.at(0)

    const historyInfectionNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_INFECTION_NOTES
    )?.at(0)

    const history60DayMd = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_MD
    )?.at(0)

    const historyMdNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_MD_NOTES
    )?.at(0)

    // Other history fields
    const functionalLimitations = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_FUNCTIONAL_LIMITATIONS
    )

    const activitiesPermitted = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_ACTIVITIES_PERMITTED
    )

    const medicalHistory = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_MEDICAL_HISTORY
    )?.at(0)

    const surgicalHistory = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_SURGICAL_HISTORY
    )?.at(0)

    return {
      [DischargeFormField.HISTORY_60_DAY_ER]: history60DayEr,
      [DischargeFormField.HISTORY_ER_NOTES]: historyErNotes,
      [DischargeFormField.HISTORY_60_DAY_HOSPITAL]: history60DayHospital,
      [DischargeFormField.HISTORY_HOSPITAL_NOTES]: historyHospitalNotes,
      [DischargeFormField.HISTORY_60_DAY_INJURIES]: history60DayInjuries,
      [DischargeFormField.HISTORY_INJURY_NOTES]: historyInjuryNotes,
      [DischargeFormField.HISTORY_60_DAY_FALLS]: history60DayFalls,
      [DischargeFormField.HISTORY_FALL_NOTES]: historyFallNotes,
      [DischargeFormField.HISTORY_60_DAY_INFECTIONS]: history60DayInfections,
      [DischargeFormField.HISTORY_INFECTION_NOTES]: historyInfectionNotes,
      [DischargeFormField.HISTORY_60_DAY_MD]: history60DayMd,
      [DischargeFormField.HISTORY_MD_NOTES]: historyMdNotes,
      [DischargeFormField.HISTORY_FUNCTIONAL_LIMITATIONS]: functionalLimitations,
      [DischargeFormField.HISTORY_ACTIVITIES_PERMITTED]: activitiesPermitted,
      [DischargeFormField.HISTORY_MEDICAL_HISTORY]: medicalHistory,
      [DischargeFormField.HISTORY_SURGICAL_HISTORY]: surgicalHistory
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.HISTORY_60_DAY_ER)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_ER,
        value: this.getFormDataField(DischargeFormField.HISTORY_60_DAY_ER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_ER_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_ER_NOTES,
        value: this.getFormDataField(DischargeFormField.HISTORY_ER_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_60_DAY_HOSPITAL)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_HOSPITAL,
        value: this.getFormDataField(DischargeFormField.HISTORY_60_DAY_HOSPITAL)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_HOSPITAL_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_HOSPITAL_NOTES,
        value: this.getFormDataField(DischargeFormField.HISTORY_HOSPITAL_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_60_DAY_INJURIES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_INJURIES,
        value: this.getFormDataField(DischargeFormField.HISTORY_60_DAY_INJURIES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_INJURY_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_INJURY_NOTES,
        value: this.getFormDataField(DischargeFormField.HISTORY_INJURY_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_60_DAY_FALLS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_FALLS,
        value: this.getFormDataField(DischargeFormField.HISTORY_60_DAY_FALLS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_FALL_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_FALL_NOTES,
        value: this.getFormDataField(DischargeFormField.HISTORY_FALL_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_60_DAY_INFECTIONS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_INFECTIONS,
        value: this.getFormDataField(DischargeFormField.HISTORY_60_DAY_INFECTIONS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_INFECTION_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_INFECTION_NOTES,
        value: this.getFormDataField(DischargeFormField.HISTORY_INFECTION_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_60_DAY_MD)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_60_DAY_MD,
        value: this.getFormDataField(DischargeFormField.HISTORY_60_DAY_MD)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_MD_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_MD_NOTES,
        value: this.getFormDataField(DischargeFormField.HISTORY_MD_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_FUNCTIONAL_LIMITATIONS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_FUNCTIONAL_LIMITATIONS,
        value: this.getFormDataField(DischargeFormField.HISTORY_FUNCTIONAL_LIMITATIONS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_ACTIVITIES_PERMITTED)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_ACTIVITIES_PERMITTED,
        value: this.getFormDataField(DischargeFormField.HISTORY_ACTIVITIES_PERMITTED)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_MEDICAL_HISTORY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_MEDICAL_HISTORY,
        value: this.getFormDataField(DischargeFormField.HISTORY_MEDICAL_HISTORY)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.HISTORY_SURGICAL_HISTORY)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_HISTORY_SURGICAL_HISTORY,
        value: this.getFormDataField(DischargeFormField.HISTORY_SURGICAL_HISTORY)
      })
    }

    return mutations
  }
}
